import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy, Delete, Download, UploadFile } from '@mui/icons-material';
import { hmiUpdatesService } from '../services/api';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const HmiUpdatesPage: React.FC = () => {
  const [releases, setReleases] = useState<any[]>([]);
  const [latestReleaseId, setLatestReleaseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canUpload = useMemo(() => {
    return !!version.trim() && !!file && !uploading;
  }, [version, file, uploading]);

  const loadReleases = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await hmiUpdatesService.getReleases();
      setReleases(data.releases);
      setLatestReleaseId(data.latestReleaseId);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'Errore caricamento release');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReleases();
  }, []);

  const handleUpload = async () => {
    if (!canUpload) return;

    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      await hmiUpdatesService.uploadRelease({
        version: version.trim(),
        notes: notes.trim(),
        file,
      });
      setSuccess('Release caricata con successo');
      setVersion('');
      setNotes('');
      setFile(null);
      await loadReleases();
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'Errore upload release');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (release: any) => {
    const confirmed = window.confirm(`Eliminare la release ${release.version}?`);
    if (!confirmed) return;

    setDeletingId(release.id);
    setError(null);
    setSuccess(null);
    try {
      await hmiUpdatesService.deleteRelease(release.id);
      setSuccess(`Release ${release.version} eliminata`);
      await loadReleases();
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'Errore eliminazione release');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (release: any) => {
    setError(null);
    setSuccess(null);
    try {
      const response = await hmiUpdatesService.downloadRelease(release.id);
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/i.exec(contentDisposition);
      const fileName = match?.[1] || release.fileName || `HMI_${release.version}.exe`;

      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`Download avviato: ${fileName}`);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'Errore download release');
    }
  };

  const handleCopyLink = async (release: any) => {
    setError(null);
    setSuccess(null);
    try {
      const data = await hmiUpdatesService.createShareLink(release.id, 24);
      const shareUrl = data?.url || `${window.location.origin}${data?.path || ''}`;
      if (!shareUrl) {
        throw new Error('Link non disponibile');
      }

      const copied = await copyTextToClipboard(shareUrl);
      if (copied) {
        setSuccess(`Link copiato negli appunti (validita ${data?.expiresHours || 24}h)`);
      } else {
        promptManualCopy(shareUrl, 'il link');
        setSuccess(`Link generato (copia manuale, validita ${data?.expiresHours || 24}h)`);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'Errore creazione link');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Aggiornamenti HMI
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Carica l&apos;eseguibile .exe della versione HMI. Le macchine con servizio aggiornamenti attivo potranno scaricare solo l&apos;ultima release disponibile.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Nuova Release</Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Versione"
            placeholder="es. 1.4.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            size="small"
          />
          <TextField
            label="Note di rilascio (opzionale)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            multiline
            minRows={2}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              Seleziona EXE
              <input
                hidden
                type="file"
                accept=".exe,application/x-msdownload,application/octet-stream"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file ? `${file.name} (${formatBytes(file.size || 0)})` : 'Nessun file selezionato'}
            </Typography>
          </Box>
          <Box>
            <Button variant="contained" onClick={handleUpload} disabled={!canUpload}>
              {uploading ? 'Caricamento...' : 'Carica Release'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Versione</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Dimensione</TableCell>
              <TableCell>SHA256</TableCell>
              <TableCell>Caricata da</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {releases.map((release) => (
              <TableRow key={release.id}>
                <TableCell>{release.version}</TableCell>
                <TableCell>{release.fileName}</TableCell>
                <TableCell>{formatBytes(release.fileSize)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {String(release.sha256 || '').slice(0, 14)}...
                </TableCell>
                <TableCell>{release.uploadedBy?.displayName || release.uploadedBy?.email || '-'}</TableCell>
                <TableCell>{new Date(release.createdAt).toLocaleString()}</TableCell>
                <TableCell>
                  {release.id === latestReleaseId ? (
                    <Chip label="Latest" color="success" size="small" />
                  ) : (
                    <Chip label="Storica" size="small" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleDownload(release)}
                    title="Scarica EXE"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleCopyLink(release)}
                    title="Copia link download"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDelete(release)}
                    disabled={deletingId === release.id}
                    title="Elimina release"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {releases.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nessuna release caricata.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HmiUpdatesPage;
